import HLSManager from './HLSManager';
import DeviceDetector from './DeviceDetector';

/**
 * Manages preloading of FMP4/HLS videos for instant TikTok-like playback
 * Uses actual HLS instances (not dummy videos) for true preloading
 */
class PreloadManager {
    constructor() {
        this.preloadedStreams = new Map(); // url -> {hls, timestamp, type}
        this.maxPreloads = 3; // Match virtual slides window (1 before + 1 current + 2 after)
        this.manifestCache = new Map(); // url -> manifest data
        this.cleanupInterval = null;
        this.startCleanup();
    }

    /**
     * Preload FMP4/HLS stream - DISABLED for simplicity
     * HLS preloading adds too much complexity, just load on demand
     */
    preloadStream(url, type = 'full') {
        console.log('[PreloadManager] HLS preloading disabled - load on demand');
        return Promise.resolve(null);

        /* DISABLED - too complex
        // Skip if already preloaded
        if (this.preloadedStreams.has(url)) {
            return Promise.resolve(this.preloadedStreams.get(url));
        }

        // Clean up old preloads if we hit the limit
        if (this.preloadedStreams.size >= this.maxPreloads) {
            this.cleanupOldest();
        }

        return new Promise((resolve, reject) => {
            try {
                // Create HLS instance without video element (preload only)
                const hlsManager = new HLSManager();

                // Create minimal video element for HLS.js (required for FMP4)
                const preloadVideo = this.createPreloadVideo();

                // Set up preload completion tracking
                let manifestLoaded = false;
                let segmentsPreloaded = 0;
                let hasError = false;
                const targetSegments = type === 'full' ? 3 : 1; // Preload 3 segments for full, 1 for quick

                // Track when preload is ready
                const checkPreloadReady = () => {
                    if (!hasError && manifestLoaded && (type === 'manifest' || segmentsPreloaded >= targetSegments)) {
                        console.log(`[PreloadManager] Preload ready (${type}): ${url}`, {
                            manifestLoaded,
                            segmentsPreloaded,
                            targetSegments
                        });
                        resolve({ hlsManager, preloadVideo, type });
                    }
                };

                // Timeout for preload to prevent hanging
                const preloadTimeout = setTimeout(() => {
                    if (!manifestLoaded) {
                        hasError = true;
                        console.warn(`[PreloadManager] Preload timeout for ${url}`);
                        this.cleanupFailedPreload(url, hlsManager, preloadVideo);
                        reject(new Error('Preload timeout'));
                    }
                }, 10000); // 10 second timeout for preload

                // Listen for HLS events to track preloading progress
                if (hlsManager.hls) {
                    hlsManager.hls.on(hlsManager.hls.constructor.Events.MANIFEST_PARSED, () => {
                        manifestLoaded = true;
                        clearTimeout(preloadTimeout);
                        checkPreloadReady();
                    });

                    hlsManager.hls.on(hlsManager.hls.constructor.Events.FRAG_LOADED, () => {
                        segmentsPreloaded++;
                        checkPreloadReady();
                    });

                    // Enhanced error handling - don't fail entire preload queue
                    hlsManager.hls.on(hlsManager.hls.constructor.Events.ERROR, (event, data) => {
                        if (!hasError) {
                            hasError = true;
                            console.warn(`[PreloadManager] Preload error for ${url}, continuing with next videos:`, {
                                type: data.type,
                                details: data.details,
                                fatal: data.fatal
                            });

                            // Clean up this failed preload
                            this.cleanupFailedPreload(url, hlsManager, preloadVideo);
                            clearTimeout(preloadTimeout);

                            // Don't reject - just resolve with null to continue preloading others
                            resolve(null);
                        }
                    });
                }

                // Initialize HLS with preload video
                const initialized = hlsManager.init(preloadVideo, url);

                if (initialized) {
                    // Store the preloading instance
                    this.preloadedStreams.set(url, {
                        hls: hlsManager,
                        video: preloadVideo,
                        timestamp: Date.now(),
                        type,
                        ready: false,
                        error: false
                    });

                    // For full preload, trigger initial segment loading then pause
                    if (type === 'full' && hlsManager.hls) {
                        // Start loading segments for fast startup
                        hlsManager.hls.startLoad();

                        // Pause loading after 3 seconds to prevent timeouts
                        setTimeout(() => {
                            if (!hasError && hlsManager.hls && !hlsManager.hls.destroyed) {
                                hlsManager.hls.stopLoad();
                                console.log(`[PreloadManager] Paused loading for ${url} to prevent timeouts`);
                            }
                        }, 3000);
                    }

                    console.log(`[PreloadManager] Started preloading (${type}): ${url}`);
                } else {
                    hasError = true;
                    this.cleanupPreloadVideo(preloadVideo);
                    clearTimeout(preloadTimeout);
                    // Don't reject - resolve with null to continue
                    resolve(null);
                }
            } catch (error) {
                console.error(`[PreloadManager] Exception during preload setup for ${url}:`, error);
                // Don't reject - resolve with null to continue preloading others
                resolve(null);
            }
        });
        */
    }

    /**
     * Clean up a failed preload
     */
    cleanupFailedPreload(url, hlsManager, preloadVideo) {
        // Mark as failed
        if (this.preloadedStreams.has(url)) {
            const preload = this.preloadedStreams.get(url);
            preload.error = true;
        }

        // Clean up resources
        if (hlsManager) {
            try {
                hlsManager.destroy();
            } catch (e) {
                console.error('[PreloadManager] Error destroying HLS manager:', e);
            }
        }

        this.cleanupPreloadVideo(preloadVideo);

        // Remove from preloaded streams
        this.preloadedStreams.delete(url);
    }

    /**
     * Get a preloaded HLS instance - DISABLED
     */
    getPreloadedStream(url) {
        // Always return null - preloading disabled
        return null;
    }

    /**
     * Create minimal video element for preloading
     */
    createPreloadVideo() {
        const video = document.createElement('video');
        video.muted = true;
        video.preload = 'auto';
        video.style.display = 'none';
        video.style.position = 'absolute';
        video.style.top = '-9999px';
        video.width = 1;
        video.height = 1;
        video.crossOrigin = 'anonymous';
        document.body.appendChild(video);
        return video;
    }
    
    /**
     * Clean up preload video element
     */
    cleanupPreloadVideo(video) {
        if (video && video.parentNode) {
            document.body.removeChild(video);
        }
    }
    
    /**
     * Remove a preloaded stream
     */
    removePreload(url) {
        const preloaded = this.preloadedStreams.get(url);
        if (preloaded) {
            if (preloaded.hls) {
                preloaded.hls.destroy();
            }
            this.cleanupPreloadVideo(preloaded.video);
            this.preloadedStreams.delete(url);
            console.log(`[PreloadManager] Removed preload: ${url}`);
        }
    }
    
    /**
     * Clean up oldest preload to make room
     */
    cleanupOldest() {
        let oldestUrl = null;
        let oldestTime = Date.now();
        
        for (const [url, data] of this.preloadedStreams.entries()) {
            if (data.timestamp < oldestTime) {
                oldestTime = data.timestamp;
                oldestUrl = url;
            }
        }
        
        if (oldestUrl) {
            this.removePreload(oldestUrl);
        }
    }

    /**
     * Start automatic cleanup of old preloads
     */
    startCleanup() {
        this.cleanupInterval = setInterval(() => {
            this.cleanupOldPreloads();
        }, 10000); // Clean every 10 seconds
    }
    
    /**
     * Clean up old preloads (older than 60 seconds for virtual slides)
     */
    cleanupOldPreloads() {
        const maxAge = 60000; // 60 seconds (longer for virtual slides)
        const now = Date.now();
        
        for (const [url, data] of this.preloadedStreams.entries()) {
            if (now - data.timestamp > maxAge) {
                console.log(`[PreloadManager] Cleaning up old preload: ${url}`);
                this.removePreload(url);
            }
        }
    }
    
    /**
     * Preload manifest only (lightweight)
     */
    preloadManifest(url) {
        return this.preloadStream(url, 'manifest');
    }
    
    /**
     * Get preload statistics
     */
    getStats() {
        return {
            active: this.preloadedStreams.size,
            maxPreloads: this.maxPreloads,
            urls: Array.from(this.preloadedStreams.keys()),
            manifestCache: this.manifestCache.size
        };
    }

    /**
     * Clear all preloads
     */
    clearAll() {
        console.log('[PreloadManager] Clearing all preloads');
        for (const url of this.preloadedStreams.keys()) {
            this.removePreload(url);
        }
        this.manifestCache.clear();
    }
    
    /**
     * Destroy manager and cleanup
     */
    destroy() {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
        this.clearAll();
    }
}

// Export singleton instance
export default new PreloadManager();